// Globals
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { categoriesSelector, createPopularCategories, getCategories, getPopularCategories } from '../../feature';

// Components
import { Select, Button } from '@/ui';
import {
  StyledPageTitle,
  StyledTabTitleWrapper,
  StyledTag,
  StyledTagWrapper,
  StyledCategoriesWrapper,
  StyledOption,
  StyledPopularCategoriesTitle,
  StyledPopularCategoriesSubTitle,
  StyledRow,
} from '../../components/Styled';
import { showErrorToast, showSuccessToast } from '@/components';
import { CrossIcon } from '@/icons';

// Models
import { ICategory } from '@/models/categories.model';

// Helpers
import { useBoolean } from '@/hooks';
import { categoriesOptions } from '../../helpers/categoriesOptions';
import { useCategoriesAutocomplete } from '../../hooks';
import { Resource, usePermissions } from '@/modules/Permissions';

const MAX_CATEGORIES = 6;

const PopularCategoriesTabContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { canManage } = usePermissions();
  const canManagePopular = canManage(Resource.POPULAR_CATEGORY);
  const { value: isEdit, setFalse: turnOffEditing, setTrue: turnOnEditing } = useBoolean();
  const { categories, popularCategories, isLoadingCategories } = useAppState(categoriesSelector);
  const { list: categoriesList, onSearch: onCategoriesSearch } = useCategoriesAutocomplete({});
  const { t } = useTranslation(['categories']);
  const list = categoriesList ?? categories;
  const [selectedCategories, setSelectedCategories] = useState<ICategory[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);

  const options = categoriesOptions(list as ICategory[])?.map((item) => ({
    value: item.id,
    label: selectedCategories.some((cat) => cat.id === item.id) ? <StyledOption>{item.name}</StyledOption> : item.name,
  })) || [list];

  const handleSelect = (id: string) => {
    const category = categoriesOptions(list as ICategory[])?.find((item) => item.id === id);
    if (category && selectedCategories.length < MAX_CATEGORIES) {
      setSelectedCategories((prev) => {
        if (prev.find((el) => el.id === category.id)) {
          return prev;
        }
        return [...prev, category];
      });
    } else {
      showErrorToast({ message: t('popular_categories_validation_error') });
    }

    setInputValue('');
    setOpen(true);
  };

  const handleRemove = (id: string) => {
    setSelectedCategories((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSubmit = async () => {
    const selectedIds = selectedCategories?.map((item) => item.id);
    const data = {
      categories: selectedIds,
    };
    try {
      await dispatch(createPopularCategories(data)).unwrap();
      setOpen(false);
      showSuccessToast({ message: t('create_popular_category_success') });
    } catch {
    } finally {
      turnOffEditing();
    }
  };

  useEffect(() => {
    dispatch(getCategories());
    dispatch(getPopularCategories());
  }, [dispatch]);

  useEffect(() => {
    if (popularCategories?.length) {
      setSelectedCategories(popularCategories);
    }
  }, [popularCategories]);

  return (
    <>
      <StyledTabTitleWrapper>
        <StyledPageTitle>{t('popular_categories_tab_title')}</StyledPageTitle>
        {canManagePopular && (
          <Button onClick={isEdit ? handleSubmit : turnOnEditing} loading={isLoadingCategories}>
            {t(isEdit ? 'create_category_save' : 'popular_categories_tab_edit_title')}
          </Button>
        )}
      </StyledTabTitleWrapper>
      <StyledCategoriesWrapper>
        <StyledPopularCategoriesTitle>{t('popular_categories_tab_title')}</StyledPopularCategoriesTitle>
        <StyledRow>
          <Select
            showSearch
            disabled={!isEdit}
            options={options}
            value={inputValue}
            onChange={(value: string) => setInputValue(value)}
            onSearch={onCategoriesSearch}
            onSelect={handleSelect}
            open={open}
            onDropdownVisibleChange={() => setOpen(true)}
            style={{ width: '32rem' }}
            autoClearSearchValue
          />
          {open && (
            <Button onClick={() => setOpen(false)}>
              <CrossIcon />
            </Button>
          )}
        </StyledRow>
        <StyledPopularCategoriesSubTitle>{t('sub_title_popular_categories')}</StyledPopularCategoriesSubTitle>
        <StyledTagWrapper>
          {selectedCategories.map(({ id, name }) => (
            <StyledTag key={id} closable onClose={() => isEdit && handleRemove(id)}>
              {name}
            </StyledTag>
          ))}
        </StyledTagWrapper>
      </StyledCategoriesWrapper>
    </>
  );
};

export default PopularCategoriesTabContainer;
